import { useState } from 'react';
import { useAuthStore } from '../../lib/auth.js';
import { ApiError } from '../../lib/api.js';
import { Button } from '../ui/Button.js';
import { SendModalShell } from '../send/SendModalShell.js';
import { editorLabel } from '../../lib/editor-label.js';
import { formatDate } from '../../lib/format.js';
import { CommissionTermsForm } from './CommissionTermsForm.js';
import {
  buildCourseCommissionConfirm,
  commissionVersionDetail,
  commissionVersionLabel,
  courseOverrideSummary,
  formatCommissionDate,
  type SetCourseCommissionBody,
} from '../../lib/commission.js';
import {
  useCourseCommissionHistory,
  useSetCourseCommissionTerms,
  type CommissionOverviewCollege,
  type CommissionOverviewCourse,
} from '../../hooks/useCommission.js';

/** Local "are you sure?" dialog — mirrors the one in CommissionDetailPanel (no shared component). */
function ConfirmDialog({
  open,
  title,
  body,
  confirmLabel,
  loading,
  onConfirm,
  onCancel,
}: {
  open: boolean;
  title: string;
  body: string;
  confirmLabel: string;
  loading: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  return (
    <SendModalShell open={open} onClose={onCancel} blocking={loading}>
      <div className="p-6">
        <h2 className="font-serif text-xl tracking-tight text-ink">{title}</h2>
        <p className="mt-2 text-sm text-muted">{body}</p>
        <div className="mt-6 flex justify-end gap-2">
          <Button variant="secondary" onClick={onCancel} disabled={loading}>
            Cancel
          </Button>
          <Button variant="primary" loading={loading} onClick={onConfirm}>
            {confirmLabel}
          </Button>
        </div>
      </div>
    </SendModalShell>
  );
}

/**
 * One course's override history. Mounting this component IS the fetch — the
 * brief wants course history on demand, so the parent only renders it once the
 * row's History is opened.
 */
function CourseOverrideHistory({
  masterCourseId,
  asOf,
}: {
  masterCourseId: string;
  asOf: string;
}) {
  const currentUserId = useAuthStore((s) => s.user?.id);
  const history = useCourseCommissionHistory(masterCourseId);
  const rows = history.data?.history ?? [];
  // The version in force = the newest whose valid_from is on/before the SERVER's
  // as-of date. Not the browser's today: commission may be future-dated, so a
  // wrong day badges a version that has not started.
  const currentVersionId = rows.find((v) => v.validFrom <= asOf)?.id ?? null;

  return (
    <div className="mt-2 rounded-lg bg-cream/50 px-3 py-2">
      {history.isLoading && <p className="text-xs text-muted">Loading history…</p>}
      {history.error && <p className="text-xs text-danger">Couldn&apos;t load the history.</p>}
      {!history.isLoading && !history.error && rows.length === 0 && (
        <p className="text-xs text-muted">No special deal has been set for this course yet.</p>
      )}
      {!history.isLoading && !history.error && rows.length > 0 && (
        <ul className="divide-y divide-line">
          {rows.map((v) => (
            <li key={v.id} className="py-1.5">
              <div className="text-xs text-ink">
                {commissionVersionLabel(v)}
                {v.id === currentVersionId && (
                  <span className="badge ml-2 bg-success-bg text-success">Current</span>
                )}
              </div>
              {commissionVersionDetail(v) && (
                <div className="text-[11px] text-muted">{commissionVersionDetail(v)}</div>
              )}
              <div className="text-[11px] text-muted">
                Effective {formatCommissionDate(v.validFrom)} · {editorLabel(v, currentUserId)} ·{' '}
                {formatDate(v.createdAt)}
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/**
 * The courses of one college (brief 5e): each row states whether it follows the
 * college deal, holds its own special deal, is ended, or explicitly inherits;
 * and offers set/edit, retire, and history on demand.
 *
 * There is no course-level "End" action — the brief asks only for set/edit,
 * retire and history. "Ended" still renders when the data says so (an override
 * can be 'none' via the API), so the vocabulary is complete either way.
 */
export function CourseOverridesSection({
  college,
  asOf,
}: {
  college: CommissionOverviewCollege;
  asOf: string;
}) {
  const setTerms = useSetCourseCommissionTerms();
  const [editingId, setEditingId] = useState<string | null>(null);
  const [historyId, setHistoryId] = useState<string | null>(null);
  // The pending payload IS the open state (the ScholarshipSection idiom): save
  // and retire share one dialog with a per-intent title, body and label.
  const [intent, setIntent] = useState<{
    course: CommissionOverviewCourse;
    body: SetCourseCommissionBody;
  } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const busy = setTerms.isPending;
  const clearBanners = () => {
    setError(null);
    setSuccess(null);
  };

  async function commit() {
    if (!intent) return;
    clearBanners();
    try {
      await setTerms.mutateAsync({
        masterCourseId: intent.course.masterCourseId,
        body: intent.body,
      });
      setSuccess(
        intent.body.kind === 'inherit'
          ? `${intent.course.courseName} follows the college deal again.`
          : `Special deal saved for ${intent.course.courseName}.`,
      );
      setIntent(null);
      setEditingId(null);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Something went wrong. Please try again.');
      // Close so the banner is actually visible rather than sitting behind the
      // modal overlay — the brief's "no dead ends" rule.
      setIntent(null);
    }
  }

  return (
    <div className="mt-6">
      <h3 className="mb-2 font-serif text-base text-ink">Courses at this college</h3>

      {college.courses.length === 0 && (
        <p className="text-sm text-muted">This college has no courses you can see.</p>
      )}

      {error && (
        <div className="mb-3 rounded-lg border border-danger/30 bg-danger-bg px-3 py-2 text-sm text-danger">
          {error}
        </div>
      )}
      {success && (
        <div className="mb-3 rounded-lg border border-success/30 bg-success-bg px-3 py-2 text-sm text-success">
          {success}
        </div>
      )}

      {college.courses.length > 0 && (
        <ul className="divide-y divide-line">
          {college.courses.map((c) => (
            <li key={c.masterCourseId} className="py-2.5">
              <div className="flex items-start gap-3">
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm text-ink">{c.courseName}</div>
                  <div className="text-xs text-muted">{courseOverrideSummary(c.override)}</div>
                  {/* Reference only: muted, labelled, and never inside a control. */}
                  {c.legacyText && (
                    <div className="mt-1">
                      <div className="text-[11px] uppercase tracking-wide text-muted">
                        Legacy note (import)
                      </div>
                      <p className="whitespace-pre-wrap text-xs text-muted">{c.legacyText}</p>
                    </div>
                  )}
                </div>
                <div className="flex shrink-0 gap-1">
                  <Button
                    size="sm"
                    variant="ghost"
                    disabled={busy}
                    onClick={() => {
                      clearBanners();
                      setEditingId(editingId === c.masterCourseId ? null : c.masterCourseId);
                    }}
                  >
                    {c.override?.kind === 'terms' ? 'Edit special deal' : 'Set special deal'}
                  </Button>
                  {/* Retire suits any SET, non-inherit override — retiring a
                      'none' is meaningful too (the course stops paying nothing
                      and follows the college again). */}
                  {c.override != null && c.override.kind !== 'inherit' && (
                    <Button
                      size="sm"
                      variant="ghost"
                      disabled={busy}
                      onClick={() =>
                        setIntent({ course: c, body: { kind: 'inherit', validFrom: asOf } })
                      }
                    >
                      Retire
                    </Button>
                  )}
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() =>
                      setHistoryId(historyId === c.masterCourseId ? null : c.masterCourseId)
                    }
                  >
                    History
                  </Button>
                </div>
              </div>

              {editingId === c.masterCourseId && (
                <CommissionTermsForm
                  idPrefix={`course-${c.masterCourseId}`}
                  groupLabel={`Special deal — ${c.courseName}`}
                  current={c.override}
                  defaultValidFrom={asOf}
                  busy={busy}
                  submitLabel="Save special deal"
                  onDirty={clearBanners}
                  onRequestSave={(body) => setIntent({ course: c, body })}
                />
              )}

              {historyId === c.masterCourseId && (
                <CourseOverrideHistory masterCourseId={c.masterCourseId} asOf={asOf} />
              )}
            </li>
          ))}
        </ul>
      )}

      <ConfirmDialog
        open={intent !== null}
        title={intent?.body.kind === 'inherit' ? 'Retire the special deal' : 'Set the special deal'}
        body={
          intent
            ? buildCourseCommissionConfirm(
                intent.course.courseName,
                intent.body,
                intent.course.override,
              )
            : ''
        }
        confirmLabel={intent?.body.kind === 'inherit' ? 'Retire' : 'Save special deal'}
        loading={busy}
        onConfirm={() => void commit()}
        onCancel={() => setIntent(null)}
      />
    </div>
  );
}
